import dayjs from "dayjs"
import { ISelectedTicket } from "types/improved-booking"
import { ItimeSlot } from "types/ticket"

/**
 * Helper functions for handling tickets in the booking process
 */

/**
 * Determines if a timeslot is still available and active
 * @param timeslot The timeslot to check
 * @returns Whether the timeslot is eligible for booking
 */
const isTimeslotEligible = (timeslot: ItimeSlot): boolean => {
    const currentDate = dayjs()
    const endDate = dayjs(timeslot.end_slot)

    // Check if the ticket is still available (not expired)
    if (!currentDate.isBefore(endDate)) {
        return false
    }

    // Check if the ticket is active
    return timeslot.status === "active"
}

/**
 * Checks if a timeslot has no tickets available
 * @param timeslot The timeslot to check
 * @returns Whether the timeslot has no tickets
 */
export const isTimeslotWithoutTickets = (
    timeslot: ItimeSlot | any
): boolean => {
    return !timeslot.types || timeslot.types.length === 0
}

/**
 * Checks if all tickets in a timeslot are sold out (limit = 0)
 * @param timeslot The timeslot to check
 * @returns Whether all tickets in the timeslot are sold out
 */
export const isTimeslotSoldOut = (timeslot: ItimeSlot | any): boolean => {
    if (isTimeslotWithoutTickets(timeslot)) {
        return true
    }

    return timeslot.types.every((type: any) => type.limit === 0)
}

/**
 * Checks if a single ticket type is sold out
 * @param ticketType The ticket type to check
 * @returns Whether the ticket type is sold out
 */
export const isTicketSoldOut = (ticketType: any): boolean => {
    return ticketType.limit === 0
}

/**
 * Checks if a timeslot should be disabled (sold out or no tickets)
 * @param timeslot The timeslot to check
 * @returns Whether the timeslot should be disabled
 */
export const isTimeslotDisabled = (timeslot: ItimeSlot | any): boolean => {
    return isTimeslotWithoutTickets(timeslot) || isTimeslotSoldOut(timeslot)
}

/**
 * Gets all available timeslots, sorted by start time
 * @param time_slots Array of timeslots to filter
 * @returns Array of eligible timeslots, sorted by start time
 */
export const getAvailableTimeslots = (time_slots: ItimeSlot[]): ItimeSlot[] => {
    return time_slots
        ?.map((timeslot) =>
            isTimeslotEligible(timeslot) ? { ...timeslot } : null
        )
        ?.filter((timeslot): timeslot is ItimeSlot => timeslot !== null)
        ?.sort(
            (a, b) =>
                new Date(a.start_slot).getTime() -
                new Date(b.start_slot).getTime()
        )
}

/**
 * Filters tickets by a specific timeslot ID
 * @param timeslotId The timeslot ID to filter by
 * @param time_slots Array of timeslots to search
 * @returns Array of ticket types for the specified timeslot
 */
export const filterTicketsByTimeslotId = (
    timeslotId: string,
    time_slots: ItimeSlot[]
): ItimeSlot["types"] => {
    const timeslot = time_slots.find((slot) => slot._id === timeslotId)
    return timeslot?.types || []
}

/**
 * Determines if the current ticket count qualifies for a promotion
 * @param ticketType The selected ticket type with promotion information
 * @param currentTicketCount The current count of tickets
 * @returns Whether the current count qualifies for a promotion
 */
export const isCountCurrentlyQualifiedForPromotion = (
    ticketType: ISelectedTicket,
    currentTicketCount: number
): boolean => {
    // If no promotion exists, return false
    if (!ticketType.promotion) {
        return false
    }

    const promotionStartCount = ticketType.promotion?.buy_qty || 0
    const promotionEndCount =
        promotionStartCount + (ticketType.promotion?.get_qty || 0)

    // Check if count is within the promotion range and we have at least one complete promotion set
    if (
        currentTicketCount >= promotionStartCount &&
        currentTicketCount < promotionEndCount
    ) {
        const fullPromotionSets = Math.floor(
            currentTicketCount / promotionStartCount
        )
        return fullPromotionSets > 0
    }

    return false
}
